import type { Observation } from '@medplum/fhirtypes'
import { formatDateNumber, formatDateTime, isDateString } from '#src/dates.ts'
import { formatClinicalUnit } from '#src/display.ts'
import type { ExtendedObservation } from '#src/models/observation.ts'

export type ValueKind = 'number' | 'date' | 'string' | 'boolean' | 'coding'

// A number with its UCUM unit code, when it has one.
export interface NumberValue {
  kind: 'number'
  value: number
  unit?: string
}

export function numberValue(value: number, unit?: string | null): NumberValue {
  const number: NumberValue = { kind: 'number', value }
  if (unit) {
    number.unit = unit
  }
  return number
}

// An ISO date (`YYYY-MM-DD`) or date-time.
export interface DateValue {
  kind: 'date'
  value: string
}

export interface StringValue {
  kind: 'string'
  value: string
}

export interface BooleanValue {
  kind: 'boolean'
  value: boolean
}

export interface CodedAnswer {
  system?: string
  code: string
  display?: string
}

// An answer with only the parts that are set.
export function codedAnswer({
  code,
  system,
  display,
}: CodedAnswer): CodedAnswer {
  const answer: CodedAnswer = { code }
  if (system) {
    answer.system = system
  }
  if (display) {
    answer.display = display
  }
  return answer
}

// A coded answer; several when more than one answer was recorded.
export interface CodedValue {
  kind: 'coding'
  value: CodedAnswer[]
}

// One recorded value, whatever its type: what conditions compare and
// derivations compute from.
export type ObservationValue =
  | NumberValue
  | DateValue
  | StringValue
  | BooleanValue
  | CodedValue

export function isEmptyValue(
  value: ObservationValue | null | undefined
): boolean {
  if (!value) {
    return true
  }
  switch (value.kind) {
    case 'number':
      return !Number.isFinite(value.value)
    case 'date':
    case 'string':
      return value.value.trim() === ''
    case 'boolean':
      return false
    case 'coding':
      return value.value.length === 0
  }
}

// The value as a clinician reads it: "120 mmHg", "03/04/1950", "Yes",
// "Continent".
export function formatValue(
  value: ObservationValue | null | undefined
): string {
  if (!value || isEmptyValue(value)) {
    return ''
  }
  switch (value.kind) {
    case 'number': {
      if (!value.unit) {
        return String(value.value)
      }
      const unit = formatClinicalUnit(value.unit)
      return unit === '%' ? `${value.value}%` : `${value.value} ${unit}`
    }
    case 'date':
      return isDateString(value.value)
        ? formatDateNumber(value.value)
        : formatDateTime(value.value)
    case 'string':
      return value.value
    case 'boolean':
      return value.value ? 'Yes' : 'No'
    case 'coding':
      return value.value.map((entry) => entry.display ?? entry.code).join(', ')
  }
}

// The single value a recorded observation holds, or null when it holds none,
// holds parts (components) instead, or was entered in error.
export function observationValueOf(
  observation: ExtendedObservation
): ObservationValue | null {
  if (observation.isEnteredInError) {
    return null
  }
  return fhirObservationValue(observation.toFhirResource())
}

// The single value a FHIR Observation holds, whether or not it was written.
export function fhirObservationValue(
  resource: Observation
): ObservationValue | null {
  const quantity = resource.valueQuantity
  if (quantity?.value !== undefined) {
    const unit = quantity.code ?? quantity.unit
    return numberValue(quantity.value, unit)
  }
  if (resource.valueInteger !== undefined) {
    return { kind: 'number', value: resource.valueInteger }
  }
  if (resource.valueDateTime) {
    return { kind: 'date', value: resource.valueDateTime }
  }
  if (resource.valueBoolean !== undefined) {
    return { kind: 'boolean', value: resource.valueBoolean }
  }
  if (resource.valueString !== undefined) {
    return { kind: 'string', value: resource.valueString }
  }
  const concept = resource.valueCodeableConcept
  if (concept) {
    const entries = (concept.coding ?? []).flatMap((coding) =>
      coding.code ? [codedAnswer({ ...coding, code: coding.code })] : []
    )
    if (entries.length) {
      return { kind: 'coding', value: entries }
    }
    return concept.text ? { kind: 'string', value: concept.text } : null
  }
  return null
}
